import { useMemo, useState } from "react";
import {
  BookOpenIcon,
  PlusIcon,
  RefreshCwIcon,
  SearchIcon,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { type SkillScope, useSkills } from "@/hooks/use-skills";
import { cn } from "@/lib/utils";
import { SettingsPageHeader } from "./settings-page-header";
import type { BreadcrumbSegment } from "./settings-page-header";

type SkillsListMode = "personal" | "admin";

type TabKey = "all" | "user" | "tenant" | "system";

const TABS: TabKey[] = ["all", "user", "tenant", "system"];

export function SkillsList({ mode = "personal" }: { mode?: SkillsListMode }) {
  const { t } = useTranslation();
  const { skills, loading, error, refresh } = useSkills();
  const [query, setQuery] = useState("");
  const [activeTab, setActiveTab] = useState<TabKey>("all");

  const normalizedQuery = query.trim().toLocaleLowerCase();
  const filteredSkills = useMemo(
    () =>
      skills.filter((skill) => {
        if (activeTab !== "all" && skill.scope !== activeTab) return false;
        if (!normalizedQuery) return true;
        return (
          skill.name.toLocaleLowerCase().includes(normalizedQuery) ||
          skill.description.toLocaleLowerCase().includes(normalizedQuery)
        );
      }),
    [skills, normalizedQuery, activeTab],
  );

  const showEmptyFilterState =
    (normalizedQuery.length > 0 || activeTab !== "all") &&
    filteredSkills.length === 0;
  const showEmptyState =
    normalizedQuery.length === 0 &&
    activeTab === "all" &&
    filteredSkills.length === 0;

  const basePath = mode === "admin" ? "/settings/admin/skills" : "/settings/skills";
  const breadcrumbs: BreadcrumbSegment[] =
    mode === "admin"
      ? [
          { labelKey: "settings.breadcrumb.root", href: "/settings" },
          { labelKey: "settings.nav.administration", href: "/settings/admin" },
          { labelKey: "settings.nav.adminSkills" },
        ]
      : [
          { labelKey: "settings.breadcrumb.root", href: "/settings" },
          { labelKey: "settings.nav.skills" },
        ];

  return (
    <div className="flex h-full flex-col">
      <div className="flex-1 overflow-y-auto px-8 pt-8 pb-6">
        <div className="mx-auto max-w-[880px] space-y-6">
          <SettingsPageHeader
            breadcrumbs={breadcrumbs}
            titleKey="settings.skills.title"
            trailing={
              <Button asChild>
                <Link to={`${basePath}/new`}>
                  <PlusIcon className="size-4" />
                  {t("settings.skills.newButton")}
                </Link>
              </Button>
            }
          />

          <div className="flex items-center gap-1 rounded-lg border border-border/60 bg-muted/30 p-1">
            {TABS.map((tab) => (
              <button
                key={tab}
                type="button"
                onClick={() => setActiveTab(tab)}
                className={cn(
                  "flex-1 rounded-md px-3 py-1.5 text-[13px] font-medium transition-colors",
                  activeTab === tab
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {t(`settings.skills.tabs.${tab}`)}
              </button>
            ))}
          </div>

          <div className="relative">
            <SearchIcon
              className="pointer-events-none absolute top-1/2 left-3.5 size-4 -translate-y-1/2 text-muted-foreground"
              strokeWidth={1.7}
            />
            <input
              type="text"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t("settings.skills.filterPlaceholder")}
              aria-label={t("settings.skills.filterPlaceholder")}
              className="h-11 w-full rounded-[10px] border border-border bg-background pr-4 pl-10 text-[14px] outline-none transition-shadow placeholder:text-muted-foreground focus:border-ring focus:shadow-[0_0_0_3px_oklch(0.55_0.03_264/0.10)]"
            />
          </div>

          {error && (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-destructive/25 bg-destructive/5 px-4 py-3 text-[13px] text-destructive">
              <span>{error}</span>
              <Button variant="outline" size="sm" onClick={refresh}>
                <RefreshCwIcon className="size-4" />
                {t("common.retry")}
              </Button>
            </div>
          )}

          {loading && skills.length === 0 ? (
            <div className="flex items-center gap-2 rounded-lg border border-border/60 bg-muted/20 px-4 py-3 text-[13px] text-muted-foreground">
              <Spinner size="sm" />
              <span>{t("common.loading")}</span>
            </div>
          ) : showEmptyFilterState ? (
            <EmptyState
              icon={SearchIcon}
              label={t("settings.skills.emptyFilterState")}
            />
          ) : showEmptyState ? (
            <EmptyState
              icon={BookOpenIcon}
              label={t("settings.skills.emptyState")}
            />
          ) : (
            <div className="grid gap-3">
              {filteredSkills.map((skill) => (
                <Link
                  key={skill.id}
                  to={`${basePath}/${skill.id}`}
                  className="block rounded-xl border border-border/60 bg-background px-4 py-4 transition-colors hover:bg-accent/40"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <h2 className="truncate font-medium text-[15px] text-foreground">
                          {skill.name}
                        </h2>
                        <ScopeBadge scope={skill.scope} />
                      </div>
                      <p
                        title={skill.description}
                        className="mt-1 truncate font-mono text-[12px] text-muted-foreground"
                      >
                        {skill.description}
                      </p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ScopeBadge({ scope }: { scope: SkillScope }) {
  const { t } = useTranslation();
  const styles: Record<SkillScope, string> = {
    user: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
    tenant:
      "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
    system:
      "bg-slate-100 text-slate-600 dark:bg-slate-800/40 dark:text-slate-400",
  };

  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-[11px] font-medium",
        styles[scope],
      )}
    >
      {t(`settings.skills.scope.${scope}`)}
    </span>
  );
}

function EmptyState({
  icon: Icon,
  label,
}: {
  icon: React.ComponentType<{ className?: string; strokeWidth?: number }>;
  label: string;
}) {
  return (
    <div className="flex flex-col items-center justify-center rounded-xl border border-border/80 border-dashed bg-muted/10 px-6 py-14 text-center">
      <Icon className="size-8 text-muted-foreground" strokeWidth={1.7} />
      <p className="mt-4 max-w-md text-[13px] text-muted-foreground">{label}</p>
    </div>
  );
}
